/**
 * This file is based on the elevenlabs-node project by FelixWaweru
 * https://github.com/FelixWaweru/elevenlabs-node
 *
 * Types are based on the official ElevenLabs API
 * https://elevenlabs.io/docs/api-reference/text-to-speech
 *
 * The motivation for the rewrite of the original projects was of course the TS types,
 * but also to make it free from dependencies. So instead of using the Axios library
 * for HTTP requests, this uses simple `fetch` calls.
 *
 * At the time of writing the shape of the JS data in the `elevenlabs-node` lib maps 1:1
 * with the TS types in this implementation. This is handy, since you can use the
 * `elevenlabs-node` in your backend code and pretty much let the HTTP Response pass through
 * to the client in most cases.
 *
 *
 * ⚠️ NOTE REGARDING API KEY USAGE
 *
 * This example implementation uses the ElevenLabs API key to access the API backend service.
 *
 *                         ~~~~ YOU SHOULD NOT USE THIS IN PRODUCTION ~~~~
 *
 * */

import {IElevenLabsSpeechDataCacheStorage} from "./ElevenLabsSpeechDataCacheStorage";
import {IElevenLabsApiClient} from "./types/IElevenLabsApiClient";
import {IElevenLabsModelData} from "./types/IElevenLabsModelData";
import {IElevenLabsApiResponseVoices} from "./types/IElevenLabsApiResponseVoices";
import {IElevenLabsVoiceData} from "./types/IElevenLabsVoiceData";
import {IElevenLabsTextToSpeechRequestData} from "./types/IElevenLabsTextToSpeechRequestData";
import {IElevenLabsVoiceSettingsData} from "./types/IElevenLabsVoiceSettingsData";
import {IElevenLabsUserInfoData} from "./types/IElevenLabsUserInfoData";
import {IElevenLabsApiClientOptions} from "./types/IElevenLabsApiClientOptions";

export class ElevenLabsApiClient implements IElevenLabsApiClient {
    private _elevenLabsAPIV1 = "https://api.elevenlabs.io/v1";
    private _apiKey: string;
    private _defaultVoiceId: string;
    private _controllers: AbortController[] = [];
    private _speechDataCacheStorage: IElevenLabsSpeechDataCacheStorage | undefined;

    constructor(options: IElevenLabsApiClientOptions, speechDataCacheStorage?: IElevenLabsSpeechDataCacheStorage) {
        this._apiKey = options.apiKey;
        this._defaultVoiceId = options.voiceId;
        this._speechDataCacheStorage = speechDataCacheStorage;
    }

    abortAllApiRequests(): void {
        this._controllers.forEach((controller) => {
            controller.abort();
        })
        this._controllers = [];
    }

    get defaultVoiceId(): string {
        return this._defaultVoiceId;
    }

    async textToSpeech(ttsRequestData: IElevenLabsTextToSpeechRequestData): Promise<ArrayBuffer> {
        ttsRequestData.modelId = ttsRequestData.modelId ? ttsRequestData.modelId : 'eleven_monolingual_v1';

        const cachedSpeechData = await this._speechDataCacheStorage?.fetch(ttsRequestData);
        if (cachedSpeechData) {
            return cachedSpeechData;
        }

        const voiceIdValue = ttsRequestData.voiceId || this._defaultVoiceId;
        const voiceURL = `${this._elevenLabsAPIV1}/text-to-speech/${voiceIdValue}`;

        const response = await this._makeApiRequest(voiceURL,
            'POST',
            {
                "Accept": "audio/mpeg",
                "xi-api-key": this._apiKey,
                "Content-Type": "application/json",
            },
            JSON.stringify(ttsRequestData)
        );

        if (response.status !== 200) {
            throw new Error('ElevenLabsApiClient.textToSpeech(): Response status was not 200. Got: ' + response.status);
        }

        const buffer = await response.arrayBuffer();

        this._speechDataCacheStorage?.add(ttsRequestData, buffer)

        return buffer;
    }

    async getVoices(): Promise<IElevenLabsVoiceData[]> {
        const voiceURL = `${this._elevenLabsAPIV1}/voices`;

        const response = await this._makeApiRequest(voiceURL, 'GET');

        if (response.status !== 200) {
            throw new Error('ElevenLabsApiClient.textToSpeechStream(): Response status was not 200. Got: ' + response.status)
        }

        const voiceList = JSON.parse(await response.text()) as IElevenLabsApiResponseVoices;
        return voiceList.voices;
    };

    async getDefaultVoiceSettings(): Promise<IElevenLabsVoiceSettingsData> {
        const settingsURL = `${this._elevenLabsAPIV1}/voices/settings/default`;
        const response = await this._makeApiRequest(settingsURL, 'GET');

        if (response.status !== 200) {
            throw new Error('ElevenLabsApiClient.getDefaultVoiceSettings(): Response status was not 200. Got: ' + response.status);
        }

        return JSON.parse(await response.text()) as IElevenLabsVoiceSettingsData;
    }

    async getVoiceSettings(voiceId: string): Promise<IElevenLabsVoiceSettingsData> {
        const settingsURL = `${this._elevenLabsAPIV1}/voices/${voiceId}/settings`;

        const response = await this._makeApiRequest(settingsURL, 'GET');

        if (response.status !== 200) {
            throw new Error('ElevenLabsApiClient.getVoiceSettings(): Response status was not 200. Got: ' + response.status);
        }

        return JSON.parse(await response.text()) as IElevenLabsVoiceSettingsData;

    }

    async getVoice(voiceId: string): Promise<IElevenLabsVoiceData> {
        const voiceURL = `${this._elevenLabsAPIV1}/voices/${voiceId}`;

        const response = await this._makeApiRequest(voiceURL, 'GET');

        if (response.status !== 200) {
            throw new Error('ElevenLabsApiClient.getVoice(): Response status was not 200. Got: ' + response.status);
        }

        return JSON.parse(await response.text()) as IElevenLabsVoiceData;
    }

    async editVoiceSettings(voiceId: string, settings: IElevenLabsVoiceSettingsData): Promise<string> {
        const settingsURL = `${this._elevenLabsAPIV1}/voices/${voiceId}/settings/edit`;

        const response = await this._makeApiRequest(settingsURL,
            'POST',
            {
                "xi-api-key": this._apiKey,
            },
            JSON.stringify(settings),
        );

        if (response.status !== 200) {
            throw new Error('ElevenLabsApiClient.editVoiceSettings(): Response status was not 200. Got: ' + response.status);
        }

        return JSON.parse(await response.text());
    }

    async getModels(): Promise<IElevenLabsModelData[]> {
        const voiceURL = `${this._elevenLabsAPIV1}/models`;

        const response = await this._makeApiRequest(voiceURL, 'GET');

        if (response.status !== 200) {
            throw new Error('ElevenLabsApiClient.getModels(): Response status was not 200. Got: ' + response.status);
        }

        return JSON.parse(await response.text()) as IElevenLabsModelData[]
    }

    async getUserInfo(): Promise<IElevenLabsUserInfoData> {
        const voiceURL = `${this._elevenLabsAPIV1}/user`;
        const response = await this._makeApiRequest(voiceURL, 'GET');

        if (response.status !== 200) {
            throw new Error('ElevenLabsApiClient.getUserInfo(): Response status was not 200. Got: ' + response.status);
        }

        return JSON.parse(await response.text()) as IElevenLabsUserInfoData;
    }

    private async _makeApiRequest(url: string, method: 'GET' | 'POST', headers: {
        [key: string]: string
    } = {}, body?: string): Promise<Response> {
        const controller = new AbortController();
        const signal = controller.signal;
        this._controllers.push(controller);

        const allHeaders = Object.assign(headers, {"xi-api-key": this._apiKey});

        const initData: RequestInit = {
            method,
            headers: allHeaders,
            body,
            signal
        }

        let response: Response;
        try {
            response = await fetch(url, initData);
        } finally {
            this._controllers = this._controllers.filter(c => c !== controller);
        }
        return response;
    }

}
